import React from 'react';
import { formatCurrency, formatCurrencyDetailed, cn } from '../../lib/utils';

/**
 * CurrencyDisplay - A responsive currency component that shrinks font for large values
 * 
 * @param {number} amount - The amount to display
 * @param {string} size - Base size: 'xs', 'sm', 'base', 'lg', 'xl', '2xl', '3xl' (default: 'xl')
 * @param {boolean} detailed - Show cents (default: false)
 * @param {string} className - Additional classes
 * @param {boolean} showSign - Show +/- sign (default: false for positive)
 * @param {string} color - Text color class (default: auto-detect based on value)
 */
export function CurrencyDisplay({ 
  amount, 
  size = 'xl', 
  detailed = false, 
  className = '',
  showSign = false,
  color = null,
  prefix = '',
  ...props 
}) {
  if (amount === null || amount === undefined) {
    return <span className={cn('font-mono', className)} {...props}>-</span>;
  }

  const absAmount = Math.abs(amount);
  const formatted = detailed ? formatCurrencyDetailed(absAmount) : formatCurrency(absAmount);
  
  // Determine font size based on value magnitude
  const getFontSize = () => {
    const sizeScales = {
      '3xl': ['text-3xl', 'text-2xl', 'text-xl', 'text-lg', 'text-base'],
      '2xl': ['text-2xl', 'text-xl', 'text-lg', 'text-base', 'text-sm'],
      'xl': ['text-xl', 'text-lg', 'text-base', 'text-sm', 'text-xs'],
      'lg': ['text-lg', 'text-base', 'text-sm', 'text-xs', 'text-xs'],
      'base': ['text-base', 'text-sm', 'text-xs', 'text-xs', 'text-xs'],
      'sm': ['text-sm', 'text-xs', 'text-xs', 'text-xs', 'text-xs'],
      'xs': ['text-xs', 'text-xs', 'text-xs', 'text-xs', 'text-xs'],
    };
    
    const scale = sizeScales[size] || sizeScales['xl'];
    
    // Character count thresholds (including $ and commas)
    // $1,234 = 6 chars, $12,345 = 7, $123,456 = 8, $1,234,567 = 10, $12,345,678 = 11
    if (absAmount >= 1000000000) return scale[4];  // $1B+ (13+ chars)
    if (absAmount >= 100000000) return scale[3];   // $100M+ (12 chars)
    if (absAmount >= 10000000) return scale[2];    // $10M+ (11 chars)
    if (absAmount >= 1000000) return scale[1];     // $1M+ (10 chars)
    return scale[0];
  };

  // Determine color
  const getColor = () => {
    if (color) return color;
    if (amount < 0) return 'text-red-600';
    return '';
  };

  // Build display string
  let displayValue = formatted;
  if (amount < 0) {
    displayValue = `-${formatted}`;
  } else if (showSign && amount > 0) {
    displayValue = `+${formatted}`;
  }
  
  if (prefix) {
    displayValue = `${prefix}${displayValue}`;
  }

  return (
    <span 
      className={cn(
        'font-mono font-bold whitespace-nowrap',
        getFontSize(),
        getColor(),
        className
      )}
      title={formatCurrencyDetailed(amount)} // Show full amount on hover
      {...props}
    >
      {displayValue}
    </span>
  );
}

export default CurrencyDisplay;
